// AUTO PLAY — the attract-mode switch every 2001 casino site had, with the brakes it did not.
//
// The hook repeats the cabinet's own play call after each round lands, with a 1.5 s pause between
// them, and stops the moment any of four brakes trips: the round limit, a win big enough to walk
// away on, a losing streak, or a balance that will not cover another round. It never invents a
// bet: it replays exactly the `allIn` and `args` the cabinet is showing right now, so changing the
// target or the stake mid-run changes the next round, not the one already in the air.
//
// Nothing here decides an outcome, and nothing here skips a step of the live lifecycle. On the
// real floor every round is still its own approval and its own signature — auto play queues the
// prompts, it does not remove them, and the panel says so.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CHIP } from 'lib/casino';
import { FONT, GroupBox, T, fmtChip, xpButton } from './ui';

const BPS = BigInt(10000);
const ALL_IN_BPS = BigInt(10500); // stake + the 5% fee-inclusive fee
const ZERO = BigInt(0);
const PAUSE_MS = 1500;
const DEFAULT_BIG_WIN_CENTI = 500; // payout ≥ 5.00x the stake is a walk-away win
const DEFAULT_LOSS_STREAK = 8;

/** The round limits the panel offers. Infinity is "until a brake trips". */
export const ROUND_CHOICES = [10, 25, 50, Infinity];

/** House.splitAllIn's stake half — what the payout multiples are quoted against. */
function stakeOf(allIn) {
  return (BigInt(allIn) * BPS) / ALL_IN_BPS;
}

function toBig(v) {
  if (v === null || v === undefined) return null;
  return BigInt(v);
}

/**
 * Why the next round must not be launched, or '' if it may be.
 * Checked before every round, including the first, so auto play never spends into a refusal.
 */
function blockedBefore(opts, doneCount, limit) {
  if (!opts.canPlay) return 'The table will not take this bet, so auto play stopped.';
  if (Number.isFinite(limit) && doneCount >= limit) return `Auto play finished ${limit} rounds.`;
  const allIn = toBig(opts.allIn);
  if (allIn === null || allIn <= ZERO) return 'There is no bet to repeat, so auto play stopped.';
  const balance = toBig(opts.balance);
  if (balance !== null && balance < allIn) return 'Not enough chips for another round, so auto play stopped.';
  const floor = toBig(opts.balanceFloor);
  if (balance !== null && floor !== null && balance - allIn < floor) {
    return `Auto play stopped at your floor of ${fmtChip(floor, 2)} ${CHIP.symbol}.`;
  }
  return '';
}

/**
 * Why the run must end after the round that just landed, or '' if it may continue.
 * `payout` and `stake` are the settled numbers from the receipt, never anything recomputed here.
 */
function blockedAfter(state) {
  const { doneCount, limit, payout, stake, stopBigWin, bigWinCenti, losses, lossStreak } = state;
  if (Number.isFinite(limit) && doneCount >= limit) return `Auto play finished ${limit} rounds.`;
  if (stopBigWin && stake > ZERO && payout * BigInt(100) >= stake * BigInt(bigWinCenti)) {
    return `Stopped on a big win — ${fmtChip(payout, 2)} ${CHIP.symbol} back.`;
  }
  if (lossStreak > 0 && losses >= lossStreak) return `Stopped after ${losses} losing rounds in a row.`;
  return '';
}

/**
 * Repeat `bet.play(allIn, args)` until a brake trips.
 *
 * `opts` is read fresh on every round (through a ref, not a dependency list) so the panel and the
 * cabinet's own controls stay live during a run: { allIn, args, canPlay, balance, balanceFloor,
 * bigWinCenti, lossStreak }.
 */
export function useAuto(bet, opts) {
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [rounds, setRounds] = useState(10);
  const [stopBigWin, setStopBigWin] = useState(true);
  const [stopReason, setStopReason] = useState('');
  // Bumping this is what guarantees a render — and therefore an effect — after every round,
  // whether the round settled, was refused at the door or left a bet stuck.
  const [roundSeq, setRoundSeq] = useState(0);

  // Latest-value refs. Assigned in the render body on purpose: the timer callback and the round
  // effect run outside the render that scheduled them and must not see a stale bet or stale opts.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const betRef = useRef(bet);
  betRef.current = bet;
  const limitRef = useRef(rounds);
  limitRef.current = rounds;
  const stopBigRef = useRef(stopBigWin);
  stopBigRef.current = stopBigWin;

  const runningRef = useRef(false);
  const timerRef = useRef(null);
  const lastIdRef = useRef(null);
  const lossesRef = useRef(0);
  const doneRef = useRef(0);

  const halt = useCallback(reason => {
    runningRef.current = false;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setRunning(false);
    setStopReason(reason);
  }, []);

  const launch = useCallback(async () => {
    timerRef.current = null;
    if (!runningRef.current) return;
    const o = optsRef.current;
    const blocked = blockedBefore(o, doneRef.current, limitRef.current);
    if (blocked) {
      halt(blocked);
      return;
    }
    try {
      // play() owns its own error handling and resolves when the round is over either way.
      // Anything that still escapes is a real fault and stops the run loudly rather than looping.
      await betRef.current.play(o.allIn, o.args);
    } catch (err) {
      halt(`Auto play stopped: ${(err && err.message) || 'the round failed'}`);
      return;
    }
    if (!runningRef.current) return;
    setRoundSeq(s => s + 1);
  }, [halt]);

  // One pass per finished round: read the receipt, count it, then continue or stop.
  useEffect(() => {
    if (roundSeq === 0 || !runningRef.current) return;
    const b = betRef.current;
    const o = optsRef.current;

    if (b.phase === 'stuck') {
      halt('That bet is placed and still open. Settle it before auto playing again.');
      return;
    }
    if (b.phase !== 'done' || !b.result) {
      halt('The round did not settle, so auto play stopped.');
      return;
    }
    const id = String(b.result.id);
    if (id === lastIdRef.current) {
      halt('The round produced no new receipt, so auto play stopped.');
      return;
    }
    lastIdRef.current = id;

    const stake = stakeOf(o.allIn);
    const payout = toBig(b.result.payout) || ZERO;
    lossesRef.current = payout > ZERO ? 0 : lossesRef.current + 1;
    doneRef.current += 1;
    setDone(doneRef.current);

    const after = blockedAfter({
      doneCount: doneRef.current,
      limit: limitRef.current,
      payout,
      stake,
      stopBigWin: stopBigRef.current,
      bigWinCenti: o.bigWinCenti || DEFAULT_BIG_WIN_CENTI,
      losses: lossesRef.current,
      lossStreak: o.lossStreak === undefined ? DEFAULT_LOSS_STREAK : o.lossStreak,
    });
    if (after) {
      halt(after);
      return;
    }
    timerRef.current = setTimeout(launch, PAUSE_MS);
  }, [roundSeq, halt, launch]);

  // Leaving the cabinet mid-run must not leave a timer holding a dead component.
  useEffect(
    () => () => {
      runningRef.current = false;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  const start = useCallback(() => {
    const o = optsRef.current;
    const blocked = blockedBefore(o, 0, limitRef.current);
    if (blocked) {
      setStopReason(blocked);
      return;
    }
    const b = betRef.current;
    lastIdRef.current = b.result ? String(b.result.id) : null;
    lossesRef.current = 0;
    doneRef.current = 0;
    setDone(0);
    setStopReason('');
    runningRef.current = true;
    setRunning(true);
    launch();
  }, [launch]);

  const stop = useCallback(() => halt('Auto play stopped.'), [halt]);

  const idle = bet.phase === 'idle' || bet.phase === 'done';
  return {
    running,
    done,
    rounds,
    setRounds,
    stopBigWin,
    setStopBigWin,
    stopReason,
    start,
    stop,
    canStart: !!opts.canPlay && !running && idle,
  };
}

/** The panel under the play button. Everything it shows comes from useAuto. */
export function AutoPanel({ auto, practice }) {
  const limit = Number.isFinite(auto.rounds) ? String(auto.rounds) : '∞';
  return (
    <Box>
      <GroupBox legend="Auto play">
        <div className="au__row">
          <span className="au__label">Rounds</span>
          {ROUND_CHOICES.map(r => (
            <button
              key={String(r)}
              type="button"
              className={`au__pick${r === auto.rounds ? ' on' : ''}`}
              disabled={auto.running}
              onClick={() => auto.setRounds(r)}
            >
              {Number.isFinite(r) ? r : '∞'}
            </button>
          ))}
        </div>

        <label className="au__check">
          <input
            type="checkbox"
            checked={auto.stopBigWin}
            onChange={e => auto.setStopBigWin(e.target.checked)}
          />
          <i className="au__box" />
          Stop on a big win (5.00x the stake or better)
        </label>

        <div className="au__row au__row--go">
          {auto.running ? (
            <button type="button" className="au__stop" onClick={auto.stop}>
              STOP
            </button>
          ) : (
            <button type="button" className="au__start" onClick={auto.start} disabled={!auto.canStart}>
              START AUTO
            </button>
          )}
          <span className="au__count">
            ROUNDS DONE <b>{auto.done}</b> / {limit}
          </span>
        </div>

        {auto.stopReason ? <div className="au__reason">{auto.stopReason}</div> : null}

        <div className="au__note">
          {practice
            ? 'Auto play waits for a real drand round every time, so a run takes about five seconds a round. It stops itself when the play stack cannot cover the next bet.'
            : 'On the live floor auto play does not skip anything: every round is its own transaction and your wallet asks you to sign it. It queues the rounds, it does not approve them.'}
        </div>
      </GroupBox>
    </Box>
  );
}

const Box = styled.div`
  .au__row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .au__row--go {
    margin-top: 10px;
  }
  .au__label {
    font: 11px ${FONT.ui};
    color: #000;
    margin-right: 2px;
  }
  .au__pick {
    ${xpButton};
    min-width: 38px;
    min-height: 21px;
    padding: 0 8px;
    font-weight: 700;
  }
  .au__pick.on {
    background: linear-gradient(180deg, #fff3c4 0%, #ffd86b 45%, #d8a72a 100%);
    border-color: #8a6a10;
  }
  .au__check {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    font: 11px ${FONT.ui};
    color: #000;
    cursor: default;
  }
  .au__check input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
  .au__box {
    width: 13px;
    height: 13px;
    box-sizing: border-box;
    border: 1px solid #1c5180;
    background: linear-gradient(180deg, #fff, #e9eff8);
    display: inline-block;
    position: relative;
    flex-shrink: 0;
  }
  .au__check input:checked + .au__box::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 0px;
    width: 4px;
    height: 8px;
    border: solid #21a121;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  .au__check input:focus + .au__box {
    outline: 1px dotted #000;
    outline-offset: 1px;
  }
  .au__start,
  .au__stop {
    ${xpButton};
    min-height: 23px;
    font-weight: 700;
    letter-spacing: 1px;
  }
  .au__start {
    background: linear-gradient(180deg, #fff3c4 0%, #ffd86b 38%, #d8a72a 62%, #b8860b 100%);
    border-color: #8a6a10;
    color: #3b2a00;
  }
  .au__start:disabled {
    background: #f1f1ec;
    border-color: #c0c0c0;
    color: #a0a0a0;
  }
  .au__stop {
    background: linear-gradient(180deg, #ffd9d9, #e8a0a0 60%, #c8102e);
    border-color: #7a0016;
    color: #fff;
    text-shadow: 0 1px 0 #7a0016;
  }
  .au__count {
    font: 11px ${FONT.mono};
    color: #222;
    letter-spacing: 1px;
  }
  .au__count b {
    color: ${T.navy};
  }
  .au__reason {
    margin-top: 8px;
    padding: 3px 6px;
    background: ${T.infobar};
    border: 1px solid ${T.grayLine};
    font: 11px ${FONT.ui};
    color: #000;
  }
  .au__note {
    margin-top: 8px;
    font-size: 10px;
    line-height: 1.55;
    color: #222;
  }
`;
